/**
 * statusConstants.js
 * ─────────────────────────────────────────────────────────────────────────────
 * SINGLE SOURCE OF TRUTH for RFP and Section status definitions.
 *
 * Mirrors the backend RFP_STATUS_DEFINITIONS and SECTION_STATUS_DEFINITIONS.
 * All components that need status colors, labels, or CSS classes should
 * import from this file instead of maintaining their own mappings.
 * ─────────────────────────────────────────────────────────────────────────────
 */

// ─── RFP-Level Status Definitions (codes 0–12) ────────────────────────────
export const RFP_STATUS = {
    0:  { label: 'RFP Uploaded',                    color: '#17a2b8', category: 'Drafting'  },
    1:  { label: 'RFP Analyzed',                    color: '#007bff', category: 'Drafting'  },
    2:  { label: 'RFP In-Progress',                 color: '#ffc107', category: 'Working'   },
    3:  { label: 'RFP Submitted to Team Lead',      color: '#6610f2', category: 'Review'    },
    4:  { label: 'RFP Proposal Finalised',          color: '#28a745', category: 'Review'    },
    5:  { label: 'RFP Submitted to Client',         color: '#20c997', category: 'Finished'  },
    6:  { label: 'Proposal Accepted by Client (WON)', color: '#28a745', category: 'Finished'},
    7:  { label: 'Proposal Rejected by Client (LOST)', color: '#dc3545', category: 'Finished'},
    8:  { label: 'RFP Pursuit Decision: Go',        color: '#28a745', category: 'Decision'  },
    9:  { label: 'RFP Pursuit Decision: No-Go',     color: '#dc3545', category: 'Decision'  },
    10: { label: 'RFP Proposal Expired',            color: '#343a40', category: 'Finished'  },
    11: { label: 'Returned for Refinement',         color: '#fd7e14', category: 'Review'    },
    12: { label: 'RFP Pursuit Decision: Pending',   color: '#6c757d', category: 'Decision'  },
};

// ─── Section-Level Status Definitions (codes 50–57) ──────────────────────
export const SECTION_STATUS = {
    50: { label: 'Section Unassigned',          color: '#6c757d', category: 'Backlog'  },
    51: { label: 'Section Assigned',            color: '#17a2b8', category: 'Working'  },
    52: { label: 'Section In-Progress',         color: '#ffc107', category: 'Working'  },
    53: { label: 'Section Submitted for Review',color: '#6610f2', category: 'Review'   },
    54: { label: 'Section TL Approved',         color: '#28a745', category: 'Review'   },
    55: { label: 'Section Revision Needed',     color: '#dc3545', category: 'Working'  },
    56: { label: 'Assigned Section Expired',    color: '#343a40', category: 'Backlog'  },
    57: { label: 'Auto-Closed',                 color: '#6c757d', category: 'Finished' },
};

// ─── Combined lookup (both RFP + Section) ────────────────────────────────
export const ALL_STATUS = { ...RFP_STATUS, ...SECTION_STATUS };

// ─── Utility: Get label from code ────────────────────────────────────────
/**
 * Returns the display label for a given numeric status code.
 * Falls back to the raw value if the code is unrecognised.
 *
 * @param {number|string} code
 * @returns {string}
 */
export const getStatusLabel = (code) => {
    const numCode = Number(code);
    return ALL_STATUS[numCode]?.label ?? String(code ?? '');
};

// ─── Utility: Get color from code ────────────────────────────────────────
/**
 * Returns the brand color hex string for a given numeric status code.
 * Defaults to a neutral grey when unrecognised.
 *
 * @param {number|string} code
 * @returns {string} hex color
 */
export const getStatusColorByCode = (code) => {
    const numCode = Number(code);
    return ALL_STATUS[numCode]?.color ?? '#6c757d';
};

// ─── Utility: Get color from label (string-based fallback) ───────────────
/**
 * Resolves the brand color for a status given only its label string.
 * Searches ALL_STATUS for a matching label; falls back to heuristic matching.
 *
 * @param {string} label
 * @returns {string} hex color
 */
export const getStatusColorByLabel = (label) => {
    if (!label) return '#6c757d';
    const lower = String(label).toLowerCase();

    // Exact match first
    const found = Object.values(ALL_STATUS).find(
        s => s.label.toLowerCase() === lower
    );
    if (found) return found.color;

    // Heuristic fallbacks
    if (lower.includes('approved') || lower.includes('accepted') || lower.includes('won') || lower.includes('finalised') || (lower.includes('go') && !lower.includes('no-go'))) return '#28a745';
    if (lower.includes('no-go') || lower.includes('rejected') || lower.includes('lost') || lower.includes('revision')) return '#dc3545';
    if (lower.includes('submitted') && lower.includes('client')) return '#20c997';
    if (lower.includes('submitted') || lower.includes('review')) return '#6610f2';
    if (lower.includes('progress')) return '#ffc107';
    if (lower.includes('assigned') && !lower.includes('unassigned')) return '#17a2b8';
    if (lower.includes('refinement')) return '#fd7e14';
    if (lower.includes('expired') || lower.includes('auto-closed') || lower.includes('closed')) return '#343a40';
    if (lower.includes('pending')) return '#6c757d';
    if (lower.includes('analyzed') || lower.includes('analysed')) return '#007bff';
    if (lower.includes('uploaded')) return '#17a2b8';
    return '#6c757d';
};

// ─── Utility: Resolve color from code OR label ───────────────────────────
/**
 * Tries to resolve color from a numeric code first, then by label string.
 *
 * @param {number|string|null} code
 * @param {string|null} label
 * @returns {string} hex color
 */
export const resolveStatusColor = (code, label) => {
    if (code !== undefined && code !== null && code !== '') {
        const color = getStatusColorByCode(code);
        if (color !== '#6c757d' || ALL_STATUS[Number(code)]) return color;
    }
    return getStatusColorByLabel(label);
};

// ─── Utility: CSS class name from status (for Proposals page badges) ──────
/**
 * Returns a CSS class name string derived from the status for use in
 * proposals cards and grids. Compatible with Proposals.css status badge styles.
 *
 * @param {number|string} codeOrLabel - numeric code or label string
 * @returns {string} CSS class name
 */
export const getStatusCssClass = (codeOrLabel) => {
    if (codeOrLabel === undefined || codeOrLabel === null) return 'draft';
    const s = String(codeOrLabel).toLowerCase();

    // Try numeric code first
    const numCode = parseInt(codeOrLabel, 10);
    if (!isNaN(numCode)) {
        if ([4].includes(numCode)) return 'finalised';
        if ([6].includes(numCode)) return 'accepted';
        if ([7, 9].includes(numCode)) return 'rejected';
        if ([5, 8].includes(numCode)) return 'submitted';
        if ([2, 11].includes(numCode)) return 'in-progress';
        if ([0, 1].includes(numCode)) return 'ai-generating';
        if ([3].includes(numCode)) return 'in-review';
        if ([10].includes(numCode)) return 'at-risk';
        if ([12].includes(numCode)) return 'pending';
        if ([50].includes(numCode)) return 'draft';
        if ([51].includes(numCode)) return 'assigned';
        if ([52].includes(numCode)) return 'in-progress';
        if ([53].includes(numCode)) return 'in-review';
        if ([54].includes(numCode)) return 'accepted';
        if ([55].includes(numCode)) return 'rejected';
        if ([56, 57].includes(numCode)) return 'at-risk';
    }

    // String fallbacks
    if (s.includes('finalis') || s.includes('finaliz')) return 'finalised';
    if (s.includes('accept') || s.includes('won')) return 'accepted';
    if (s.includes('reject') || s.includes('lost') || s.includes('no-go')) return 'rejected';
    if (s.includes('submit') && s.includes('client')) return 'submitted';
    if (s.includes('go') && !s.includes('no-go')) return 'submitted';
    if (s.includes('progress')) return 'in-progress';
    if (s.includes('analyz') || s.includes('upload')) return 'ai-generating';
    if (s.includes('review') || s.includes('approval') || s.includes('refinement')) return 'in-review';
    if (s.includes('expire') || s.includes('closed')) return 'at-risk';
    if (s.includes('pending')) return 'pending';
    return 'draft';
};
